import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { formatUserName, prettyPlayerId } from 'app/core/utilities/user-util';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import User from 'entity/User';
import {
  BaseUser,
  SubscribeMessage,
  OutgoingMessageType,
  UnsubscribeMessage,
  PeerCandidate,
  SubscribeCandidateMessage,
  IncomingMessage,
  IncomingMessageType,
  OutgoingMessage,
  SubscribeAnswerMessage,
} from 'messages/websocket.messages';

import { MediaStreamWrapper, RoomParticipant } from '../../preciate-stream/room-participant';
import { ReconnectionStateType } from '../../preciate-stream/types';
import { socialAppActions, socialAppSelectors } from '../../state/app';
import { SubscriptionState } from '../../types/webrtc';
import { SceneService } from '../scene.service';

import {
  findNewSubscriptions,
  findRemovedSubscriptions,
  getLogComplement,
} from './../subscription/subscription.helper';
import { SubscriptionService } from './../subscription/subscription.service';
import { SubscriberState, SubscriptionReason, SubscriptionType } from './../subscription/subscription.types';
import { WebsocketService } from './../websocket.service';

const INVALID_PUBLISH_TIME = 0;

interface Peer {
  id: number;
  connection: RTCPeerConnection;
}

export type CrossRoomType = BaseUser | User;

interface CrossParticipant {
  user: BaseUser;
  peer?: Peer;
  closePeer?: Peer;
  publishPeerTime?: number;
  subscriptionState: SubscriptionState;
  peerConnectionTime?: number;
  reconnectionState?: ReconnectionStateType;
  retrySubscription: boolean;
  reason: SubscriptionReason;
}

// Maps userId to participants media stream
export type ParticipantMediaStreams = Record<string, MediaStreamWrapper>;

@Injectable({
  providedIn: 'root',
})
export class CrossRoomParticipantService extends Loggable {
  private rtcConfig?: RTCConfiguration;
  private social?: Social;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public crossParticipants: Map<string, CrossParticipant> = new Map<string, CrossParticipant>();

  private subscribers: SubscriberState[] = [];
  private subscriptions = new Map<string, boolean>();
  private videoStreams: ParticipantMediaStreams = {};

  private crossRoomParticipantVideoStreamSource = new BehaviorSubject<ParticipantMediaStreams>({});
  public crossRoomParticipantVideoStream$ = this.crossRoomParticipantVideoStreamSource.asObservable();

  /* protocols to notify changes within transmissions */
  public participantConnected = new Subject<RoomParticipant>();
  public participantDisconnected = new Subject<RoomParticipant>();

  public constructor(
    public websocketService: WebsocketService,
    private store: Store<AppState>,
    private sceneService: SceneService,
    private subscriptionService: SubscriptionService,
  ) {
    super();
    this.setDisplayName('CrossRoomParticipantService');
  }

  public init(): void {
    this.info(`Initializing cross room participant service`);
    this.destroyed$ = new ReplaySubject(1);

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((social) => (this.social = social));

    this.store
      .select(socialAppSelectors.getGallery)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((gallery) => {
        this.handleParticipants(gallery, SubscriptionReason.GALLERY);
      });

    this.websocketService.message$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((msg) => this.handleWebsocketMessage(msg));

    this.subscriptionService.subscription$.pipe(takeUntil(this.destroyed$)).subscribe((subs) => {
      // Check if the subscription has an existing cross room participant
      const filteredSubscriptions: SubscriberState[] = subs.filter((sub) => {
        return this.crossParticipants.has(sub.id);
      });

      const newSubscriptions = findNewSubscriptions(this.subscribers, filteredSubscriptions);
      const removedSubscriptions = findRemovedSubscriptions(this.subscribers, filteredSubscriptions);

      // If the current cross participant map is empty
      // Return and set subscribers  = []
      if (this.crossParticipants.size === 0) {
        this.subscribers = [];
        return;
      }

      this.subscribeUsers(newSubscriptions);
      this.unsubscribeUsers(removedSubscriptions);
      this.subscribers = filteredSubscriptions;
    });
  }

  private handleParticipants(array: CrossRoomType[], reason: SubscriptionReason): void {
    const filteredArray: BaseUser[] = [];
    const participants = this.sceneService.sceneManager?.participantsAndPlayerList ?? [];
    const ids = participants.map((user) => user.userId);
    array.forEach((user) => {
      const { userId } = user ?? {};
      if (!ids.includes(userId ?? '') && this.isBaseUser(user)) {
        filteredArray.push(user);
      }
    });
    filteredArray.forEach((user) => {
      const { publishing, socialId, userId } = user;
      if (userId && socialId && publishing) {
        if (!this.crossParticipants.has(userId) && this.social?.socialId !== socialId) {
          this.crossParticipants.set(userId, {
            user,
            subscriptionState: SubscriptionState.NOT_SUBSCRIBED,
            publishPeerTime: INVALID_PUBLISH_TIME,
            reconnectionState: ReconnectionStateType.INITIAL,
            retrySubscription: false,
            reason,
          });
        }
      }
    });
  }

  private isBaseUser(obj: CrossRoomType): obj is BaseUser {
    return 'socialId' in obj;
  }

  private unsubscribeUsers(subscribers: SubscriberState[]): void {
    subscribers.forEach((subscriber) => {
      const { id, reason } = subscriber;
      const participant = this.crossParticipants.get(id);
      if (!participant) {
        return;
      }

      const participantName = formatUserName(participant.user);
      this.info(`Unsubscribing from ${participantName}`);
      this.setMediaNeeded(participant, reason, true);
      this.unsubscribe(participant);
    });
  }

  private subscribeUsers(subscribers: SubscriberState[]): void {
    subscribers.forEach((subscriber) => {
      const { id, reason } = subscriber;
      const participant = this.crossParticipants.get(id);
      if (!participant) {
        this.warn('There is currently no participant for this subscriber');
        return;
      }

      const participantName = formatUserName(participant.user);
      const { socialId } = participant.user;
      this.setReason(participant, reason);

      this.info(`Subscribing to ${participantName} ${getLogComplement(subscriber)}`);
      if (!socialId) {
        return;
      }
      this.subscribe(participant);
    });
  }

  private subscribe(participant: CrossParticipant): void {
    const { cameraOn, socialId, userId } = participant.user;
    if (!userId || !socialId) {
      return;
    }

    if (participant.peer) {
      this.warn('Already subscribed: ', userId);
      return;
    }

    const subId = userId;
    if (!subId) {
      this.info('Participant removed - not subscribing', userId);
      return;
    }

    participant.subscriptionState = SubscriptionState.SUBSCRIBING;

    const peerId = Date.now();
    const config = this.createRtcConfig(participant);
    const conn = new RTCPeerConnection(config);

    const peer = {
      connection: conn,
      id: peerId,
    };
    participant.peer = peer;
    this.crossParticipants.set(userId, participant);
    this.setReason(participant, participant.reason);

    conn.onconnectionstatechange = () => {
      const { connectionState } = conn;
      this.info('Subscribe state change', { userId, connectionState, subId, peerId });
      switch (connectionState) {
        case 'connected':
          participant.subscriptionState = SubscriptionState.SUBSCRIBED;
          participant.peerConnectionTime = Date.now();
          this.crossParticipants.set(userId, participant);
          this.setReason(participant, participant.reason);
          break;

        case 'failed':
          participant.subscriptionState = SubscriptionState.FAILED;
          this.crossParticipants.set(userId, participant);
          this.setReason(participant, participant.reason);
          this.retrySubscription(participant);
          break;
        case 'disconnected':
          this.closeRtcPeer(participant, conn);
          break;
        default:
          break;
      }
    };

    conn.oniceconnectionstatechange = (): void => {
      const { iceConnectionState } = conn;
      this.info('Subscribe ICE state change', { iceConnectionState, subId, peerId });
    };

    conn.onicegatheringstatechange = (): void => {
      const { iceGatheringState } = conn;
      this.info('Subscribe ICE gathering state change', {
        iceGatheringState,
        subId,
        peerId,
      });
    };

    this.crossParticipants.set(userId, participant);
    conn.onicecandidateerror = (e) => this.trace('Candidate error', subId, e);
    conn.ontrack = (event) => this.onTrack(event, userId);
    conn.onicecandidate = (event): void => {
      this.sendCandidate(event, peerId, participant);
    };
    const v = cameraOn ?? false;
    const a = false;
    this.info('Subscribe', { userId, subId, videoNeeded: v, audioNeeded: false, peerId });

    const msg: SubscribeMessage = { t: OutgoingMessageType.SUB, subId, v, a, fromSocialId: socialId, pt: peerId };
    this.websocketService.sendMessage(msg);
    this.subscriptions.set(subId, true);
  }

  private unsubscribe(participant: CrossParticipant): boolean {
    const { peer, user } = participant;
    const userId = user.userId;
    if (!participant.peer) {
      return false;
    }

    if (participant.closePeer) {
      this.info('Closing peer already set', { closePeerId: participant.closePeer.id });
    }

    participant.closePeer = peer;
    participant.peer = undefined;
    this.crossParticipants.set(userId, participant);
    this.removeSubscription(userId);

    if (peer) {
      const msg: UnsubscribeMessage = {
        t: OutgoingMessageType.UNSUB,
        subId: userId,
        pt: peer.id,
      };
      this.websocketService.sendMessage(msg);
    }
    return true;
  }

  private retrySubscription(participant: CrossParticipant): void {
    // Only retry to subscribe once
    // Otherwise return
    if (participant.retrySubscription) {
      return;
    }
    setTimeout(() => {
      participant.subscriptionState = SubscriptionState.RESUBSCRIBE;
      participant.retrySubscription = true;
      this.unsubscribe(participant);
      this.subscribe(participant);
    }, 1500);
  }

  private onTrack(event: RTCTrackEvent, userId: string): void {
    const stream = event.streams[0];
    if (event.track.kind === 'video') {
      if (stream) {
        this.videoStreams[userId] = stream;
      } else {
        delete this.videoStreams[userId];
      }
      this.crossRoomParticipantVideoStreamSource.next(this.videoStreams);
    }
  }

  private sendCandidate(event: RTCPeerConnectionIceEvent, peerTime: number, participant: CrossParticipant): void {
    const { candidate } = event;
    const { user } = participant;
    if (!candidate) {
      this.info('ICE Candidate was null, done');
      return;
    }

    if (!user.userId) {
      this.error('sendCandidate: userId is not set when subscribing candidate');
      return;
    }
    if (!candidate.sdpMid) {
      this.error('Candidate sdpMid is unexpectedly null', candidate);
      return;
    }

    this.info('ICE Candidate received', candidate.toJSON());
    const msg: SubscribeCandidateMessage = {
      t: OutgoingMessageType.SUBCAN,
      subId: user.userId,
      d: candidate as PeerCandidate,
      pt: peerTime,
    };
    this.websocketService.sendMessage(msg);
  }

  public createRtcConfig(participant: CrossParticipant, preferredRTCConfig?: RTCConfiguration): RTCConfiguration {
    let relay = false;
    let forceTcp = false;

    // Prefer the passed in configuration to the one supplied by VideoRoomService
    const config = preferredRTCConfig ?? this.rtcConfig;

    switch (participant.reconnectionState) {
      case ReconnectionStateType.FORCE_RELAY:
        relay = true;
        break;

      case ReconnectionStateType.FORCE_TCP:
        relay = true;
        forceTcp = true;
        break;

      case ReconnectionStateType.INITIAL:
        break;

      default:
        break;
    }

    let { iceServers } = config || { iceServers: [] };

    // If forcing TCP, remove any entries with a UDP transport param
    if (forceTcp) {
      iceServers = iceServers?.filter(({ urls }) => {
        const urlArray = Array.isArray(urls) ? urls : [urls];
        return !urlArray.map((u) => new URL(u)).some((u) => u.searchParams.get('transport') === 'udp');
      });
    }
    const rtcConfig = {
      ...config,
      iceTransportPolicy: relay ? 'relay' : undefined,
      iceServers,
    } as unknown as RTCConfiguration;

    return rtcConfig;
  }

  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.CLOSE_PEER: {
        const { pt: peerId, userId } = msg;
        const participant = this.crossParticipants.get(userId);
        if (!participant) {
          return;
        }
        this.closePeerConnection(participant, peerId);
        break;
      }

      case IncomingMessageType.SERVER_ERROR: {
        const { errorText, relatedMessage } = msg;

        this.handleServerError(errorText, relatedMessage as OutgoingMessage);
        break;
      }

      case IncomingMessageType.SUBCAN: {
        const { d, pt: peerTime, subId } = msg;
        const participant = this.crossParticipants.get(subId);
        if (!participant) {
          return;
        }
        const candidate = new RTCIceCandidate(d);
        this.addCandidate(candidate, peerTime, participant).catch((e) =>
          this.error('Adding candidate failed', { subId, candidate, e }),
        );
        return;
      }

      case IncomingMessageType.SUBOFF: {
        const { d, pt: peerTime, subId } = msg;
        const participant = this.crossParticipants.get(subId);
        if (!participant) {
          return;
        }
        this.addOffer(d, peerTime, participant).catch((e) => {
          this.warn('Error adding offer', { subId, e, peerTime });
        });
        return;
      }
    }
  };

  private async addOffer(
    offer: RTCSessionDescriptionInit,
    offerPeerId: number,
    participant: CrossParticipant,
  ): Promise<void> {
    if (!participant) {
      this.error('addOffer: Could not add offer, userId is not set');
      return;
    }
    const { userId } = participant.user;
    if (!participant.peer) {
      this.warn('addOffer: Already unsubscribed, discarding offer.', userId);
      return;
    }

    if (participant.peer.id !== offerPeerId) {
      this.warn('addOffer: Offer peer id mismatch. Discarding offer as not from this subscription', {
        userId,
        peerId: participant.peer.id,
        offerPeerId,
      });
      return;
    }
    await participant.peer.connection.setRemoteDescription(offer);

    // If unsubscribe is called during the set of remote description, peer can be undefined
    if (!participant.peer) {
      return;
    }
    participant.peer.connection.getTransceivers().forEach((t) => (t.direction = 'recvonly'));
    const answer = await participant.peer.connection.createAnswer();
    const { sdp, type } = answer;
    if (!type || !sdp) {
      this.error('addOffer: Got unexpected undefined value in answer', answer);
      return;
    }

    // If unsubscribe is called during the answer creation, peer can be undefined
    if (!participant.peer) {
      return;
    }
    await participant.peer.connection.setLocalDescription(answer);
    const msg: SubscribeAnswerMessage = {
      t: OutgoingMessageType.SUBANS,
      subId: userId,
      answer: { type, sdp },
      pt: offerPeerId,
    };

    this.websocketService.sendMessage(msg);
  }

  private async addCandidate(
    candidate: RTCIceCandidate,
    candidatePeerId: number,
    participant: CrossParticipant,
  ): Promise<void> {
    const { userId } = participant.user;
    if (!participant.peer) {
      this.warn('Already unsubscribed for candidate', userId);
      return;
    }

    if (participant.peer.id !== candidatePeerId) {
      this.warn('Candidate peer time mismatch', { userId, peerId: participant.peer.id, candidatePeerId });
      return;
    }
    await participant.peer.connection.addIceCandidate(candidate);
  }

  private closePeerConnection(participant: CrossParticipant, peerId?: number): void {
    const { userId } = participant.user;
    if (!participant.closePeer) {
      this.warn('closePeerConnection: No peer to close ', { userId, peerId });
      return;
    }

    if (peerId !== undefined && participant.closePeer.id !== peerId) {
      this.info('closePeerConnection: Peer id mismatch. Skipping peer close', {
        userId,
        closePeerId: participant.closePeer?.id,
        peerId,
      });
      return;
    }

    participant.subscriptionState = SubscriptionState.UNSUBSCRIBED;

    this.info('closePeerConnection: Close peer', { userId, peerId });
    this.closeRtcPeer(participant, participant.closePeer?.connection, true);
    participant.closePeer = undefined;
    this.crossParticipants.set(userId, participant);
    this.setReason(participant, participant.reason);

    return;
  }

  private handleServerError(errorText: string, relatedMessage: OutgoingMessage): void {
    const { t } = relatedMessage;
    switch (t) {
      case OutgoingMessageType.SUB: {
        const { subId } = relatedMessage;
        // Fail the subscription if the server returns an error.
        const participant = this.crossParticipants.get(subId);

        if (participant) {
          const { userId } = participant.user;
          this.info('Handled Server error, subscription failed', { userId, errorText, subId });
          participant.subscriptionState = SubscriptionState.FAILED;
          this.crossParticipants.set(userId, participant);
          this.setReason(participant, participant.reason);
          this.retrySubscription(participant);
        }
        return;
      }

      case OutgoingMessageType.UNSUB: {
        const { pt: peerId, subId } = relatedMessage;
        const participant = this.crossParticipants.get(subId);
        // Close the peer if the server returns an error on unsubscribe
        if (participant) {
          const { userId } = participant.user;
          this.info('Handled Server error, unsubscribe failed', { userId, errorText, subId });
          this.closePeerConnection(participant, peerId);
        }
      }
    }
  }

  public removeSubscription(userId: string): void {
    this.crossParticipants.delete(userId);
    this.subscriptions.delete(userId);
    this.store.dispatch(socialAppActions.REMOVE_SUBSCRIPTION({ subscriptionId: userId }));
  }

  private setMediaNeeded(participant: CrossParticipant, reason: SubscriptionReason, unsubscribe = false): void {
    const { publishPeerTime, subscriptionState, user } = participant;
    const videoNeeded = user.cameraOn ?? false;
    this.store.dispatch(
      socialAppActions.CHANGE_SUBSCRIPTION_STATUS({
        status: {
          audioNeeded: false,
          id: user.userId,
          name: prettyPlayerId(user),
          reason: reason,
          state: subscriptionState,
          type: SubscriptionType.PARTICIPANT,
          videoNeeded: unsubscribe ? false : videoNeeded,
          peerTime: publishPeerTime ?? 0,
        },
      }),
    );
  }

  public setReason(participant: CrossParticipant, reason?: SubscriptionReason): void {
    const { publishPeerTime, subscriptionState, user } = participant;
    this.store.dispatch(
      socialAppActions.CHANGE_SUBSCRIPTION_STATUS({
        status: {
          audioNeeded: false,
          id: user.userId,
          name: prettyPlayerId(user),
          reason: reason,
          state: subscriptionState,
          type: SubscriptionType.PARTICIPANT,
          videoNeeded: user.cameraOn ?? false,
          peerTime: publishPeerTime ?? 0,
        },
      }),
    );
  }

  private closeRtcPeer = (participant: CrossParticipant, conn?: RTCPeerConnection, closePeer = false): void => {
    const { userId } = participant.user;
    if (!conn || !participant.peer?.connection) {
      return;
    }

    if (closePeer && !participant.closePeer?.connection) {
      return;
    }
    conn.onconnectionstatechange = null;
    conn.ondatachannel = null;
    conn.onicecandidate = null;
    conn.onicecandidateerror = null;
    conn.oniceconnectionstatechange = null;
    conn.onicegatheringstatechange = null;
    conn.onnegotiationneeded = null;
    conn.onsignalingstatechange = null;
    conn.ontrack = null;
    conn.close();

    if (closePeer && participant.closePeer?.connection) {
      participant.closePeer.connection = conn;
    } else {
      participant.peer.connection = conn;
    }
    this.crossParticipants.set(userId, participant);
  };

  public destroy(): void {
    this.info('Destroying cross room participant service');
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.subscribers = [];
    this.crossParticipants.clear();
  }
}
